import {useState} from 'react'
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  View,
} from 'react-native'
import {Ionicons} from '@expo/vector-icons'
import {nanoid} from 'nanoid/non-secure'

import {type ThemeEditorProps} from '#/types/capsule'

const PRIMARY = '#6c5ce7'

function makeId(): string {
  return nanoid(8)
}

export function EditorComponent({
  capsule,
  onCapsuleUpdate: _onCapsuleUpdate,
  onAddPage,
  onDeletePage,
  onPageUpdate,
  onSettingsUpdate,
}: ThemeEditorProps) {
  const [activePageId, setActivePageId] = useState<string | null>(
    capsule.slides[0]?.id ?? null,
  )

  const activePage = capsule.slides.find(p => p.id === activePageId)
  const settings = capsule.behaviorSettings as {
    instantFeedback?: boolean
    showProgressBar?: boolean
    shuffleQuestions?: boolean
    primaryColor?: string
  }

  const addSingleChoice = () => {
    const id = makeId()
    onAddPage('single_choice')
    setActivePageId(id)
  }

  const addTrueFalse = () => {
    onAddPage('true_false')
  }

  const updateOption = (pageId: string, idx: number, label: string) => {
    const page = capsule.slides.find(p => p.id === pageId)
    if (!page || !page.options) return
    const newOptions = page.options.map((o, i) =>
      i === idx ? {...o, label} : o,
    )
    onPageUpdate(pageId, {options: newOptions})
  }

  const toggleCorrect = (pageId: string, idx: number) => {
    const page = capsule.slides.find(p => p.id === pageId)
    if (!page || !page.options) return
    const newOptions = page.options.map((o, i) => ({
      ...o,
      isCorrect: i === idx,
    }))
    onPageUpdate(pageId, {options: newOptions})
  }

  const addOption = (pageId: string) => {
    const page = capsule.slides.find(p => p.id === pageId)
    if (!page || !page.options || page.options.length >= 6) return
    onPageUpdate(pageId, {
      options: [...page.options, {id: makeId(), label: '', isCorrect: false}],
    })
  }

  const removeOption = (pageId: string, idx: number) => {
    const page = capsule.slides.find(p => p.id === pageId)
    if (!page || !page.options || page.options.length <= 2) return
    onPageUpdate(pageId, {
      options: page.options.filter((_, i) => i !== idx),
    })
  }

  return (
    <View style={s.root}>
      {/* Sidebar */}
      <View style={s.sidebar}>
        <ScrollView showsVerticalScrollIndicator={false}>
          {capsule.slides.map((page, i) => (
            <Pressable
              accessibilityRole="button"
              key={page.id}
              style={[s.slide, activePageId === page.id && s.slideActive]}
              onPress={() => setActivePageId(page.id)}>
              <Text style={s.slideNum}>{i + 1}</Text>
              <Text style={s.slideType} numberOfLines={1}>
                {page.type === 'true_false' ? 'T/F' : 'MCQ'}
              </Text>
            </Pressable>
          ))}
          <Pressable
            accessibilityRole="button"
            style={s.addSlide}
            onPress={addSingleChoice}>
            <Ionicons name="add" size={20} color={PRIMARY} />
          </Pressable>
          <Pressable
            accessibilityRole="button"
            style={[s.addSlide, {marginTop: 4}]}
            onPress={addTrueFalse}>
            <Text style={{color: PRIMARY, fontSize: 11, fontWeight: '700'}}>
              T/F
            </Text>
          </Pressable>
        </ScrollView>
      </View>

      {/* Editor */}
      <ScrollView style={s.editor} contentContainerStyle={s.editorContent}>
        {activePage ? (
          <>
            <Text style={s.label}>Question</Text>
            <TextInput
              accessibilityLabel="Text input field"
              accessibilityHint="Enter your question text"
              style={s.input}
              value={activePage.question ?? ''}
              onChangeText={v => onPageUpdate(activePage.id, {question: v})}
              placeholder="Enter your question…"
              multiline
            />

            {activePage.type === 'true_false' ? (
              <>
                <Text style={s.label}>Correct answer</Text>
                <View style={s.tfRow}>
                  {(['true', 'false'] as const).map(val => (
                    <Pressable
                      accessibilityRole="button"
                      key={val}
                      style={[
                        s.tfBtn,
                        activePage.answer === val && {
                          backgroundColor: PRIMARY + '22',
                          borderColor: PRIMARY,
                        },
                      ]}
                      onPress={() =>
                        onPageUpdate(activePage.id, {answer: val})
                      }>
                      <Text
                        style={[
                          s.tfLabel,
                          activePage.answer === val && {color: PRIMARY},
                        ]}>
                        {val === 'true' ? '✓ True' : '✗ False'}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              </>
            ) : (
              <>
                <Text style={s.label}>Options</Text>
                {(activePage.options ?? []).map((opt, idx) => (
                  <View key={opt.id} style={s.optRow}>
                    <Pressable
                      accessibilityRole="button"
                      style={[
                        s.correctDot,
                        opt.isCorrect && {backgroundColor: PRIMARY},
                      ]}
                      onPress={() => toggleCorrect(activePage.id, idx)}
                    />
                    <TextInput
                      accessibilityLabel="Text input field"
                      accessibilityHint="Enter option text"
                      style={s.optInput}
                      value={opt.label}
                      onChangeText={v => updateOption(activePage.id, idx, v)}
                      placeholder={`Option ${idx + 1}`}
                    />
                    <Pressable
                      accessibilityRole="button"
                      onPress={() => removeOption(activePage.id, idx)}
                      style={s.removeBtn}>
                      <Ionicons name="close" size={14} color="#888" />
                    </Pressable>
                  </View>
                ))}
                {(activePage.options?.length ?? 0) < 6 && (
                  <Pressable
                    accessibilityRole="button"
                    style={s.addOpt}
                    onPress={() => addOption(activePage.id)}>
                    <Ionicons name="add" size={14} color={PRIMARY} />
                    <Text style={s.addOptTxt}>Add option</Text>
                  </Pressable>
                )}
              </>
            )}

            <Text style={s.label}>Explanation (optional)</Text>
            <TextInput
              accessibilityLabel="Text input field"
              accessibilityHint="Enter explanation for the correct answer"
              style={s.input}
              value={activePage.explanation ?? ''}
              onChangeText={v => onPageUpdate(activePage.id, {explanation: v})}
              placeholder="Explain the correct answer…"
              multiline
            />

            <Pressable
              accessibilityRole="button"
              style={s.deleteBtn}
              onPress={() => onDeletePage(activePage.id)}>
              <Ionicons name="trash-outline" size={14} color="#ef4444" />
              <Text style={s.deleteTxt}>Delete slide</Text>
            </Pressable>
          </>
        ) : (
          <View style={s.empty}>
            <Text style={s.emptyTxt}>No slides yet. Tap + to add one.</Text>
          </View>
        )}

        {/* Settings */}
        <View style={s.settingsBox}>
          <Text style={s.settingsTitle}>Settings</Text>
          {[
            {key: 'instantFeedback', label: 'Instant feedback'},
            {key: 'showProgressBar', label: 'Progress bar'},
            {key: 'shuffleQuestions', label: 'Shuffle questions'},
          ].map(({key, label}) => (
            <View key={key} style={s.settingRow}>
              <Text style={s.settingLabel}>{label}</Text>
              <Switch
                value={Boolean(settings[key as keyof typeof settings])}
                onValueChange={v =>
                  onSettingsUpdate({...capsule.behaviorSettings, [key]: v})
                }
                trackColor={{true: PRIMARY}}
              />
            </View>
          ))}
        </View>
      </ScrollView>
    </View>
  )
}

const s = StyleSheet.create({
  root: {flex: 1, flexDirection: 'row', backgroundColor: '#0d1117'},
  sidebar: {
    width: 64,
    backgroundColor: '#161b22',
    borderRightWidth: 1,
    borderRightColor: '#30363d',
    paddingVertical: 12,
    alignItems: 'center',
    gap: 8,
  },
  slide: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: '#21262d',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
    borderWidth: 1.5,
    borderColor: 'transparent',
  },
  slideActive: {borderColor: PRIMARY, backgroundColor: PRIMARY + '22'},
  slideNum: {fontSize: 12, fontWeight: '700', color: '#fff'},
  slideType: {fontSize: 8, color: '#8b949e'},
  addSlide: {
    width: 44,
    height: 44,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: PRIMARY + '66',
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  editor: {flex: 1},
  editorContent: {padding: 20, gap: 10},
  label: {
    color: '#8b949e',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    marginTop: 8,
  },
  input: {
    backgroundColor: '#161b22',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#30363d',
    color: '#fff',
    padding: 12,
    fontSize: 14,
    minHeight: 44,
  },
  tfRow: {flexDirection: 'row', gap: 10},
  tfBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: '#30363d',
    alignItems: 'center',
  },
  tfLabel: {fontSize: 14, fontWeight: '700', color: '#8b949e'},
  optRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  correctDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: PRIMARY,
    backgroundColor: 'transparent',
  },
  optInput: {
    flex: 1,
    backgroundColor: '#161b22',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#30363d',
    color: '#fff',
    padding: 10,
    fontSize: 13,
  },
  removeBtn: {padding: 4},
  addOpt: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: PRIMARY + '44',
    borderStyle: 'dashed',
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  addOptTxt: {color: PRIMARY, fontSize: 12, fontWeight: '600'},
  deleteBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 16,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
    backgroundColor: '#ef444418',
    borderWidth: 1,
    borderColor: '#ef444444',
    alignSelf: 'flex-start',
  },
  deleteTxt: {color: '#ef4444', fontSize: 13, fontWeight: '600'},
  empty: {alignItems: 'center', paddingVertical: 40},
  emptyTxt: {color: '#8b949e', fontSize: 14},
  settingsBox: {
    marginTop: 24,
    backgroundColor: '#161b22',
    borderRadius: 14,
    padding: 16,
    gap: 12,
    borderWidth: 1,
    borderColor: '#30363d',
  },
  settingsTitle: {
    color: '#8b949e',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  settingLabel: {color: '#fff', fontSize: 14},
})
